/**
 * Editing one article, in the app, next to the page it produces.
 *
 * Markdown on the left, the real renderer on the right: the preview is built
 * from blocks the backend parsed, not from a second Markdown implementation in
 * the browser, so what an author sees is what a publish will store.
 *
 * Draft and publish are separate on purpose. A draft may be unrenderable — a
 * half-finished rewrite has to survive a closed tab — while a publish may not,
 * and is refused outright if someone else published in the meantime.
 *
 * Each language is a separate body of text with its own versions, so the
 * switcher is how a translation gets written at all. The editor is remounted per
 * language by its caller, which is what keeps the seed-once rule below honest.
 */

import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  IconAlertTriangle,
  IconLoader2,
  IconPencil,
  IconX,
} from "@tabler/icons-react"
import { useLayoutEffect, useRef, useState } from "react"
import {
  helpEditErrorCode,
  useHelpArticlePreview,
  useHelpArticleSource,
  usePublishHelpArticle,
  useSaveHelpDraft,
} from "../api/helpAdminApi"
import type { HelpArticleEdit, HelpArticleType } from "../api/types"
import { ArticleBlocks } from "./ArticleBlocks"
import { HelpdeskEditorHints } from "./HelpdeskEditorHints"
import { HelpdeskInsertToolbar } from "./HelpdeskInsertToolbar"

const PREVIEW_DEBOUNCE_MS = 400

interface DraftState {
  title: string
  description: string
  bodyMarkdown: string
  minutes: number
  type: HelpArticleType
  featured: boolean
  changeNote: string
}

const EMPTY: DraftState = {
  title: "",
  description: "",
  bodyMarkdown: "",
  minutes: 3,
  type: "howto",
  featured: false,
  changeNote: "",
}

function Field({
  label,
  children,
  htmlFor,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="text-xs text-grey5">
        {label}
      </Label>
      {children}
    </div>
  )
}

export function HelpdeskArticleEditor({
  articleId,
  articleTitle,
  lang,
  languages,
  onLangChange,
  onClose,
}: {
  articleId: string
  /** Fallback heading for the preview until a title has been typed. */
  articleTitle?: string
  lang: string
  /** Offered in the header; omit to hide the switcher entirely. */
  languages?: readonly string[]
  onLangChange?: (lang: string) => void
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const [draft, setDraft] = useState<DraftState>(EMPTY)
  const [baseVersionNo, setBaseVersionNo] = useState<number | null>(null)
  // An action held back until unsaved work is confirmed away. Closing and
  // switching language both discard, so they share one gate.
  const [pendingDiscard, setPendingDiscard] = useState<(() => void) | null>(
    null,
  )
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const source = useHelpArticleSource(articleId, lang, true)
  const saveDraft = useSaveHelpDraft(articleId, lang)
  const publish = usePublishHelpArticle(articleId, lang)

  // Seeded once per mount, never on a refetch. Saving a draft invalidates this
  // query, so reseeding would revert whatever was typed while the save was in
  // flight — and after a publish conflict it would overwrite the very text the
  // conflict message promises is kept. The article page remounts the editor when
  // the article or language changes, so once is enough. Before paint rather than
  // after, so the form is never shown empty for a frame and then filled in.
  const seeded = useRef(false)
  const sourceData = source.data
  useLayoutEffect(() => {
    if (!sourceData || seeded.current) return
    seeded.current = true
    setDraft({
      title: sourceData.title,
      description: sourceData.description,
      bodyMarkdown: sourceData.bodyMarkdown,
      minutes: sourceData.minutes,
      type: sourceData.type,
      featured: sourceData.featured,
      // Seeded, not blanked: the draft already carries a note, and starting empty
      // would write null over it on the next save.
      changeNote: sourceData.changeNote ?? "",
    })
    setBaseVersionNo(sourceData.baseVersionNo)
  }, [sourceData])

  const debouncedBody = useDebouncedValue(
    draft.bodyMarkdown,
    PREVIEW_DEBOUNCE_MS,
  )
  const preview = useHelpArticlePreview(
    articleId,
    lang,
    debouncedBody,
    Boolean(sourceData),
  )

  const update = <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  /**
   * Drop a snippet in at the cursor. A fence only parses when it opens on its
   * own line, so the blank line before one is made here rather than left to the
   * author to remember.
   */
  const insert = (snippet: string) => {
    const field = bodyRef.current
    const body = draft.bodyMarkdown
    const start = field?.selectionStart ?? body.length
    const end = field?.selectionEnd ?? body.length
    const before = body.slice(0, start)
    const after = body.slice(end)
    const isBlock = snippet.startsWith(":::")
    const lead =
      !isBlock || !before || before.endsWith("\n\n")
        ? ""
        : before.endsWith("\n")
          ? "\n"
          : "\n\n"

    update("bodyMarkdown", `${before}${lead}${snippet}${after}`)
    const caret = before.length + lead.length + snippet.length
    requestAnimationFrame(() => {
      field?.focus()
      field?.setSelectionRange(caret, caret)
    })
  }

  const payload = (): HelpArticleEdit => ({
    title: draft.title,
    description: draft.description,
    bodyMarkdown: draft.bodyMarkdown,
    minutes: draft.minutes,
    type: draft.type,
    featured: draft.featured,
    changeNote: draft.changeNote.trim() || null,
    baseVersionNo,
    // What this editor actually opened. A publish promotes the draft row only
    // when it came from there, so independently written text cannot consume
    // somebody else's unfinished draft.
    basedOnDraft: sourceData?.hasDraft ?? false,
  })

  const handleSaveDraft = async () => {
    try {
      await saveDraft.mutateAsync(payload())
      toast.success(translate("HELPDESK/EDITOR_DRAFT_SAVED"))
    } catch (error) {
      // Never silent. A refused save looks exactly like a successful one from
      // the author's chair, and the work is in a box they are about to close.
      toast.error(
        translate(
          helpEditErrorCode(error) === "HELPDESK_DRAFT_CONFLICT"
            ? "HELPDESK/EDITOR_DRAFT_CONFLICT"
            : "HELPDESK/EDITOR_DRAFT_FAILED",
        ),
      )
    }
  }

  const handlePublish = async () => {
    try {
      await publish.mutateAsync(payload())
      toast.success(translate("HELPDESK/EDITOR_PUBLISHED"))
      onClose()
    } catch (error) {
      // Deliberately no refetch: the author's text stays, and so does the
      // version they opened on, so a retry is refused again rather than
      // quietly publishing over whoever got there first.
      if (helpEditErrorCode(error) === "HELPDESK_STALE_VERSION") {
        toast.error(translate("HELPDESK/EDITOR_STALE"))
      }
    }
  }

  // Compared against the last state the server confirmed, so reopening a saved
  // draft, or saving one, does not read as unsaved work.
  const isDirty =
    sourceData !== undefined &&
    (draft.title !== sourceData.title ||
      draft.description !== sourceData.description ||
      draft.bodyMarkdown !== sourceData.bodyMarkdown ||
      draft.minutes !== sourceData.minutes ||
      draft.type !== sourceData.type ||
      draft.featured !== sourceData.featured ||
      draft.changeNote !== (sourceData.changeNote ?? ""))

  const guard = (action: () => void) =>
    isDirty ? setPendingDiscard(() => action) : action()
  const requestClose = () => guard(onClose)

  const isBusy = saveDraft.isPending || publish.isPending
  const canPublish =
    Boolean(draft.title.trim()) &&
    Boolean(draft.bodyMarkdown.trim()) &&
    preview.data?.ok === true &&
    !isBusy

  if (source.isError) {
    return (
      <div className="flex h-full items-center justify-center px-M">
        <Typography as="p" variant="textS" className="text-white/60">
          {translate("HELPDESK/EDITOR_LOAD_ERROR")}
        </Typography>
      </div>
    )
  }

  // No editable form before the text arrives: it would be typed into and then
  // overwritten the moment it does.
  if (!sourceData) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-grey1">
      <div className="flex shrink-0 flex-wrap items-center gap-M border-b border-white/6 px-L py-S">
        {/* The language is named, not inferred: an author who opened a page that
          fell back to German is editing the German original, not writing a
          translation, and only the label makes that visible. */}
        <span className="flex items-center gap-1.5 text-grey5">
          <IconPencil size={14} />
          <Typography as="span" variant="textXS">
            {translate("HELPDESK/EDITOR_HEADING", {
              lang: lang.toUpperCase(),
            })}
          </Typography>
        </span>
        {languages && onLangChange && languages.length > 1 ? (
          <span className="flex items-center gap-1">
            {languages.map((code) => (
              <button
                key={code}
                type="button"
                disabled={isBusy}
                aria-pressed={code === lang}
                onClick={() => guard(() => onLangChange(code))}
                className={cn(
                  "rounded-full border px-2 py-0.5 text-[11px] transition-colors",
                  code === lang
                    ? "border-yellow1/40 text-yellow1"
                    : "border-white/10 text-white/40 hover:text-white/70",
                )}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </span>
        ) : null}
        {sourceData?.hasDraft ? (
          <span className="rounded-full bg-yellow1/15 px-2 py-0.5 text-[11px] text-yellow1">
            {sourceData.draftUpdatedBy
              ? translate("HELPDESK/EDITOR_DRAFT_BADGE_BY", {
                  author: sourceData.draftUpdatedBy,
                })
              : translate("HELPDESK/EDITOR_DRAFT_BADGE")}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="text-grey5 hover:text-white"
            onClick={requestClose}
            disabled={isBusy}
          >
            <IconX size={14} className="mr-1" />
            {translate("HELPDESK/EDITOR_CANCEL")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleSaveDraft()}
            disabled={
              isBusy || !draft.title.trim() || !draft.bodyMarkdown.trim()
            }
          >
            {saveDraft.isPending ? (
              <IconLoader2 size={14} className="mr-1.5 animate-spin" />
            ) : null}
            {translate("HELPDESK/EDITOR_SAVE_DRAFT")}
          </Button>
          <Button
            size="sm"
            onClick={() => void handlePublish()}
            disabled={!canPublish}
          >
            {publish.isPending ? (
              <IconLoader2 size={14} className="mr-1.5 animate-spin" />
            ) : null}
            {translate("HELPDESK/EDITOR_PUBLISH")}
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-2">
        <div className="min-w-0 overflow-y-auto border-white/6 px-M py-L lg:border-r lg:px-L">
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-M">
            <Field
              label={translate("HELPDESK/EDITOR_TITLE")}
              htmlFor="help-title"
            >
              <Input
                id="help-title"
                value={draft.title}
                maxLength={255}
                onChange={(event) => update("title", event.target.value)}
              />
            </Field>

            <Field
              label={translate("HELPDESK/EDITOR_DESCRIPTION")}
              htmlFor="help-description"
            >
              <Textarea
                id="help-description"
                rows={2}
                value={draft.description}
                maxLength={1000}
                onChange={(event) => update("description", event.target.value)}
              />
            </Field>

            <div className="grid grid-cols-1 gap-M sm:grid-cols-2">
              <Field
                label={translate("HELPDESK/EDITOR_MINUTES")}
                htmlFor="help-minutes"
              >
                <Input
                  id="help-minutes"
                  type="number"
                  min={1}
                  max={120}
                  value={draft.minutes}
                  onChange={(event) =>
                    update("minutes", Number(event.target.value) || 1)
                  }
                />
              </Field>
              <Field
                label={translate("HELPDESK/EDITOR_TYPE")}
                htmlFor="help-type"
              >
                <Select
                  value={draft.type}
                  onValueChange={(value) =>
                    update("type", value as HelpArticleType)
                  }
                >
                  <SelectTrigger id="help-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="howto">
                      {translate("HELPDESK/EDITOR_TYPE_HOWTO")}
                    </SelectItem>
                    <SelectItem value="overview">
                      {translate("HELPDESK/EDITOR_TYPE_OVERVIEW")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="flex items-center justify-between gap-M rounded-lg border border-white/6 px-M py-3">
              <div className="flex flex-col">
                <Label htmlFor="help-featured" className="text-xs text-white">
                  {translate("HELPDESK/EDITOR_FEATURED")}
                </Label>
                <Typography
                  as="span"
                  variant="textXS"
                  className="text-white/40"
                >
                  {translate("HELPDESK/EDITOR_FEATURED_HINT")}
                </Typography>
              </div>
              <Switch
                id="help-featured"
                checked={draft.featured}
                onCheckedChange={(checked) => update("featured", checked)}
              />
            </div>

            <Field
              label={translate("HELPDESK/EDITOR_BODY")}
              htmlFor="help-body"
            >
              <HelpdeskInsertToolbar onInsert={insert} />
              <Textarea
                id="help-body"
                ref={bodyRef}
                rows={22}
                spellCheck={false}
                value={draft.bodyMarkdown}
                onChange={(event) => update("bodyMarkdown", event.target.value)}
                className="font-mono text-[13px] leading-relaxed"
              />
            </Field>

            <HelpdeskEditorHints />

            <Field
              label={translate("HELPDESK/EDITOR_CHANGE_NOTE")}
              htmlFor="help-change-note"
            >
              <Input
                id="help-change-note"
                value={draft.changeNote}
                maxLength={500}
                placeholder={translate("HELPDESK/EDITOR_CHANGE_NOTE_HINT")}
                onChange={(event) => update("changeNote", event.target.value)}
              />
            </Field>
          </div>
        </div>

        <div className="min-w-0 overflow-y-auto px-M py-L lg:px-L">
          <div className="mx-auto w-full max-w-3xl">
            <div className="mb-M flex items-center gap-2">
              <Typography as="h3" variant="textXS-bold" className="text-grey5">
                {translate("HELPDESK/EDITOR_PREVIEW")}
              </Typography>
              {preview.isFetching ? (
                <IconLoader2 size={12} className="animate-spin text-grey5" />
              ) : null}
            </div>

            {preview.data && !preview.data.ok ? (
              <div className="mb-M flex items-start gap-2 rounded-lg border border-red1/30 bg-red1/10 px-M py-3">
                <IconAlertTriangle
                  size={14}
                  className="mt-0.5 shrink-0 text-red1"
                />
                <div className="min-w-0">
                  <Typography as="p" variant="textXS" className="text-white">
                    {translate("HELPDESK/EDITOR_CANNOT_RENDER")}
                  </Typography>
                  <Typography
                    as="p"
                    variant="textXS"
                    className="mt-0.5 wrap-break-word text-white/60"
                  >
                    {preview.data.error}
                  </Typography>
                </div>
              </div>
            ) : null}

            {preview.data?.warnings.length ? (
              <div className="mb-M flex flex-col gap-2 rounded-lg border border-white/8 bg-grey2 px-M py-3">
                <Typography
                  as="p"
                  variant="textXS-bold"
                  className="text-white/70"
                >
                  {translate("HELPDESK/EDITOR_COPY_CHECK")}
                </Typography>
                {preview.data.warnings.map((warning) => (
                  <div key={`${warning.rule}-${warning.excerpt}`}>
                    <Typography
                      as="p"
                      variant="textXS"
                      className="text-white/70"
                    >
                      {warning.message}
                    </Typography>
                    <Typography
                      as="p"
                      variant="textXS"
                      className="wrap-break-word text-white/35"
                    >
                      {warning.excerpt}
                    </Typography>
                  </div>
                ))}
              </div>
            ) : null}

            <Typography as="h1" variant="titleM" className="mb-4">
              {draft.title || articleTitle || articleId}
            </Typography>

            {preview.data?.blocks.length ? (
              <ArticleBlocks blocks={preview.data.blocks} />
            ) : (
              <Typography as="p" variant="textXS" className="text-white/40">
                {translate("HELPDESK/EDITOR_PREVIEW_EMPTY")}
              </Typography>
            )}
          </div>
        </div>
      </div>

      {pendingDiscard ? (
        <ConfirmDialog
          variant="warning"
          title={translate("HELPDESK/EDITOR_DISCARD_TITLE")}
          message={translate("HELPDESK/EDITOR_DISCARD_BODY")}
          confirmLabel={translate("HELPDESK/EDITOR_DISCARD_CONFIRM")}
          cancelLabel={translate("HELPDESK/EDITOR_DISCARD_CANCEL")}
          onConfirm={() => {
            const action = pendingDiscard
            setPendingDiscard(null)
            action()
          }}
          onCancel={() => setPendingDiscard(null)}
        />
      ) : null}
    </div>
  )
}
